import type {
    AnimationId,
    MemberId,
    MissionEditionInput,
    MissionId,
    MissionSession,
    MissionSessionEditionInput,
    MissionSessionLight,
    MissionStatus,
    MissionSummary,
    MissionTypeId,
    MissionTypeLight,
} from '@wwf/sdk';

export type MissionInput = Partial<
    Omit<MissionEditionInput, 'type' | 'animationIds' | 'sessions'>
> & {
    id?: MissionId;
    type?: MissionTypeLight | MissionTypeId;
    animations?: AnimationId[] | null;
    sessions?: MissionSessionEditionInput[];
    status?: MissionStatus;
    report?: string | null;
    reportSentDate?: string | null;
    startDate?: string | null;
    endDate?: string | null;
};

function missionSessionToInput(
    session: MissionSession
): MissionSessionEditionInput {
    const { participantIds: _participantIds, ...edition } = session;
    return edition;
}

export function missionSummaryToInput(summary: MissionSummary): MissionInput {
    const {
        createdAt,
        updatedAt,
        organizationId,
        contact,
        antenna,
        type,
        recordSerialized,
        totalTouchedPersonCount,
        totalMascotHugCount,
        nbParticipants,
        nbMedias,
        nbNotes,
        startDate,
        endDate,
        animations,
        sessions,
        ...rest
    } = summary;

    return {
        ...rest,
        type: type?.id,
        antennaId: antenna?.id ?? null,
        contactId: contact?.id ?? null,
        animations: animations ?? null,
        sessions: sessions?.map(missionSessionToInput),
    };
}

export function missionInputToEditionInput(
    input: MissionInput
): MissionEditionInput {
    const typeId: MissionTypeId | undefined =
        input.type == null
            ? undefined
            : typeof input.type === 'string'
              ? input.type
              : input.type.id;

    return {
        name: input.name ?? '',
        whoCanSee: input.whoCanSee ?? 'VOLUNTEER_OPEN',
        whoCanParticipate: input.whoCanParticipate ?? 'VOLUNTEER_CLOSE',
        description: input.description ?? null,
        contactId: input.contactId ?? null,
        antennaId: input.antennaId ?? null,
        address: input.address ?? null,
        participantLimit: input.participantLimit ?? null,
        withSales: input.withSales ?? null,
        partnerInfo: input.partnerInfo ?? null,
        recordSerialized: input.recordSerialized ?? null,
        visioConferenceLink: input.visioConferenceLink ?? null,
        studyFormId: input.studyFormId ?? null,
        type: typeId as unknown as MissionEditionInput['type'],
        animationIds: input.animations ?? null,
        sessions: input.sessions ?? null,
    };
}

/** React-admin `useGetOne('mission', …)` query key (see ra-core `useGetOne`). */
export function missionSummaryGetOneQueryKey(missionId: MissionId | string) {
    return ['mission', 'getOne', { id: String(missionId) }] as const;
}

/** Distinct participant member ids across all sessions (mission-level participant list). */
export function collectDistinctParticipantIdsFromMissionSessions(
    sessions: MissionSessionLight[] | null | undefined
): MemberId[] {
    if (!Array.isArray(sessions)) return [];
    const set = new Set<string>();
    for (const row of sessions) {
        const pids = row.participantIds;
        if (!Array.isArray(pids)) continue;
        for (const id of pids) {
            if (typeof id === 'string' && id.length > 0) set.add(id);
        }
    }
    return [...set] as MemberId[];
}
